/**
 * Auth error display component.
 *
 * Shows user-friendly error messages for authentication issues.
 */

import { AlertCircle, RefreshCw, X } from 'lucide-react';
import { observer } from 'mobx-react-lite';

import type { AuthErrorCode } from '@idle-depths/shared';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/Button';
import { useStores } from '@/stores/StoreContext';

/**
 * Map error codes to user-friendly messages.
 */
const ERROR_MESSAGES: Record<AuthErrorCode, { title: string; description: string }> = {
  oauth_failed: {
    title: 'Login Failed',
    description: 'There was a problem signing in with Google. Please try again.',
  },
  oauth_cancelled: {
    title: 'Login Cancelled',
    description: 'You cancelled the sign-in process. You can try again when ready.',
  },
  invalid_state: {
    title: 'Security Error',
    description: 'The login request was invalid. Please try signing in again.',
  },
  session_expired: {
    title: 'Session Expired',
    description: 'Your session has expired. Please sign in again to continue.',
  },
  network_error: {
    title: 'Connection Error',
    description: "Couldn't reach the server. You're playing in offline mode.",
  },
  server_error: {
    title: 'Server Error',
    description: 'Something went wrong on our end. Please try again later.',
  },
  unknown: {
    title: 'Error',
    description: 'An unexpected error occurred. Please try again.',
  },
};

interface AuthErrorDisplayProps {
  /** Custom class name */
  className?: string;
  /** Show retry button for retriable errors */
  showRetry?: boolean;
  /** Callback when retry is clicked */
  onRetry?: () => void;
}

/**
 * Displays authentication errors with appropriate messaging.
 *
 * Shows different UI based on error type:
 * - Dismissible alerts for most errors
 * - Retry button for network errors
 */
export const AuthErrorDisplay = observer(function AuthErrorDisplay({
  className,
  showRetry = true,
  onRetry,
}: AuthErrorDisplayProps): React.ReactElement | null {
  const { authStore } = useStores();

  if (!authStore.error) {
    return null;
  }

  const { code, message } = authStore.error;
  const errorInfo = ERROR_MESSAGES[code];
  const isRetriable = code === 'network_error' || code === 'server_error';

  const handleDismiss = (): void => {
    authStore.clearError();
  };

  const handleRetry = (): void => {
    authStore.clearError();
    if (onRetry) {
      onRetry();
    } else {
      // Default: re-initialize auth
      void authStore.initialize();
    }
  };

  return (
    <Alert variant="destructive" className={className}>
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{errorInfo.title}</AlertTitle>
      <AlertDescription className="mt-2">
        <p>{errorInfo.description}</p>
        {message && message !== errorInfo.description && (
          <p className="mt-1 text-xs opacity-70">Details: {message}</p>
        )}

        <div className="mt-3 flex gap-2">
          {showRetry && isRetriable && (
            <Button variant="outline" size="sm" onClick={handleRetry}>
              <RefreshCw className="mr-2 h-3 w-3" />
              Try Again
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={handleDismiss}>
            <X className="mr-2 h-3 w-3" />
            Dismiss
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
});
